import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useAuth } from '../../auth/AuthContext';
import { useTenantKey } from '../../lib/useTenantKey';
import { AlertBanner } from '../../components/ui/AlertBanner';
import { PLATFORM_META } from '../social/platforms';
import { signInEndingSoon } from '../social/signInExpiry';
import { instagramAccountsWithoutInbox } from './inboxAccess';

/** Natural-language join: "a", "a and b", "a, b, and c". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * Inline setup/health alerts for the inbox surfaces, so users understand why
 * messages, comments & mentions might not be flowing. Reuses the pages' existing
 * `['social-connections', tenantKey]` query (react-query dedupes). Renders nothing while
 * data is still loading, so a false alert never flashes.
 */
export function InboxSetupAlerts({ variant }: { variant: 'messages' | 'connections' }) {
  const { principal } = useAuth();
  const tenantKey = useTenantKey();
  const user = principal?.user;

  const connectionsQuery = useQuery({
    queryKey: ['social-connections', tenantKey],
    queryFn: ({ signal }) => api.listSocialConnections(signal),
    enabled: tenantKey !== '',
  });

  if (!user) return null;

  // a) Reconnect (messages only) — connected accounts whose token hard-failed refresh, or whose
  //    sign-in has ended with nothing to renew it (the API works that out from its end date).
  const reconnectConns =
    variant === 'messages' && connectionsQuery.data
      ? connectionsQuery.data.filter((c) => c.connected && c.needsReconnect === true)
      : [];
  const showReconnect = reconnectConns.length > 0;
  const reconnectNames = reconnectConns.map(
    (c) => c.accountName?.trim() || PLATFORM_META[c.platform].label,
  );
  const reconnectMessage =
    reconnectConns.length === 1
      ? `${reconnectNames[0]} needs reconnecting — its messages, comments & mentions are paused.`
      : `${joinNames(reconnectNames)} need reconnecting — their messages, comments & mentions are paused.`;

  // a2) Sign-ins that end within a week (messages only - the Connections page says it on the card).
  //     One banner per day they end on, soonest first, so each sentence is true of every account in it.
  const expiring = new Map<number, string[]>();
  if (variant === 'messages') {
    for (const c of connectionsQuery.data ?? []) {
      const days = signInEndingSoon(c);
      if (days == null) continue;
      const names = expiring.get(days) ?? [];
      names.push(c.accountName?.trim() || PLATFORM_META[c.platform].label);
      expiring.set(days, names);
    }
  }
  const expiringBanners = [...expiring.entries()]
    .sort(([a], [b]) => a - b)
    .map(([days, names]) => ({
      days,
      message: `${joinNames(names)} sign-in ends ${
        days <= 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`
      } — reconnect to keep publishing.`,
    }));

  // a3) Instagram accounts connected with Instagram Login that have no inbox access yet (messages
  //     only - on Connections the account's card has the "Enable inbox" button itself).
  const noInbox =
    variant === 'messages' ? instagramAccountsWithoutInbox(connectionsQuery.data ?? []) : [];
  const noInboxNames = noInbox.map((c) => c.accountName?.trim() || PLATFORM_META[c.platform].label);
  const noInboxMessage =
    noInbox.length === 1
      ? `${noInboxNames[0]} isn't set up for the Messages inbox yet — its messages, comments & mentions won't appear until inbox access is turned on.`
      : `${joinNames(noInboxNames)} aren't set up for the Messages inbox yet — their messages, comments & mentions won't appear until inbox access is turned on.`;

  // b) The missing-Apify-key warning is not an inbox notice: it shows in the Add source dialog
  //    (SourceFormModal) when an Apify actor is picked.

  // b2) Optional features that are off (messages only): what this page cannot show for an account
  //     until the feature is turned on in Connections - one notice per account and feature, each
  //     leading there. `=== false` (not `!truthy`): an account without the feature, or an older
  //     payload without the field, says nothing.
  const setupPrompts: { key: string; message: string }[] = [];
  if (variant === 'messages') {
    for (const c of connectionsQuery.data ?? []) {
      if (!c.connected) continue;
      const name = c.accountName?.trim() || PLATFORM_META[c.platform].label;
      if (c.optionalFeatures?.messaging === false) {
        setupPrompts.push({
          key: `${c.id}:messaging`,
          message: `${name} isn't set up for the Messenger inbox. Turn it on in Connections.`,
        });
      }
      if (c.optionalFeatures?.engagement === false) {
        setupPrompts.push({
          key: `${c.id}:engagement`,
          message: `${name} isn't set up for comments & mentions. Turn them on in Connections.`,
        });
      }
      if (c.optionalFeatures?.threadMentions === false) {
        setupPrompts.push({
          key: `${c.id}:threadMentions`,
          message: `${name} isn't set up to show Threads mentions. Turn it on in Connections.`,
        });
      }
    }
  }

  // c) Direct messages two platforms don't offer apps — ONE notice for it, one sentence each, on
  //    both Messages and Connections. LinkedIn has no approved way for apps to read or send direct
  //    messages (its comments, mentions and publishing use LinkedIn's official API and are
  //    unaffected); Threads has no direct-message API at all (its replies, mentions and publishing
  //    are unaffected). A third platform joins this same banner, never a second one.
  const showDirectMessageNotice = true;

  if (!showReconnect && !showDirectMessageNotice) return null;

  // On Messages the notices sit above the inbox on a page that does not scroll: they get a box of
  // their own that scrolls once the page needs their height for the inbox (MessagesPage decides how
  // much). A box that scrolls is a Tab stop - the last notice has no link to Tab to - and is named
  // for a screen reader.
  const ownScrollBox = variant === 'messages';

  return (
    <div
      className={cn('space-y-2', ownScrollBox && 'mb-4 overflow-y-auto')}
      {...(ownScrollBox ? { role: 'region', 'aria-label': 'Inbox notices', tabIndex: 0 } : {})}
    >
      {showReconnect && (
        <AlertBanner tone="error" action={{ label: 'Reconnect', to: '/connections' }}>
          {reconnectMessage}
        </AlertBanner>
      )}
      {expiringBanners.map(({ days, message }) => (
        <AlertBanner key={days} tone="warning" action={{ label: 'Reconnect', to: '/connections' }}>
          {message}
        </AlertBanner>
      ))}
      {noInbox.length > 0 && (
        <AlertBanner tone="warning" action={{ label: 'Turn on', to: '/connections' }}>
          {noInboxMessage}
        </AlertBanner>
      )}
      {setupPrompts.map(({ key, message }) => (
        <AlertBanner key={key} tone="info" action={{ label: 'Open Connections', to: '/connections' }}>
          {message}
        </AlertBanner>
      ))}
      {showDirectMessageNotice && (
        <AlertBanner tone="info">
          <span className="block">
            LinkedIn direct messages aren&apos;t available here — LinkedIn doesn&apos;t offer apps an
            approved way to read or send them. LinkedIn comments, mentions and publishing are
            unaffected.
          </span>
          <span className="mt-1 block">
            Threads direct messages are unavailable — Threads doesn&apos;t offer a way for outside
            apps to read or send them at this time. Threads replies, mentions and publishing are
            unaffected. We plan to revisit this if Threads makes it available.
          </span>
        </AlertBanner>
      )}
    </div>
  );
}
